import { ErrorCodeType } from '@cpd-app/shared-module/constants';

/**
 * GraphQL `extensions.code` values that correspond to an HTTP-ish status the app
 * already knows how to handle. Unguarded resolvers (getSession, refreshSession)
 * throw raw GraphQLErrors carrying only `code`, with no `originalError`.
 *
 * WHY FORBIDDEN MAPS TO SessionExpired, WHICH LOOKS WRONG AND IS NOT.
 *
 * Measured against the dev gateway: a request with an EXPIRED token, a request
 * with a bogus token, and a request with NO token all come back identically as
 * `Forbidden resource`, `code: FORBIDDEN`, `originalError.statusCode: 403`.
 * The gateway never returns 401. So the client has no way to tell "your session
 * lapsed, renew it" from "you are not allowed to read this", and mapping
 * FORBIDDEN to SessionExpired is the only behavior that lets a lapsed session
 * recover at all.
 *
 * This has been read three times as a bug worth fixing here. It is not fixable
 * here: separating the two needs the gateway to answer an authentication
 * failure with 401 and keep 403 for a permissions failure. Until it does,
 * treating every denial as refreshable is the lesser error, and the damage it
 * used to cause is handled in SessionInterceptor.refreshRequest, which no
 * longer tears down a session that was never refreshable in the first place.
 */
// Null prototype so hostile/odd code values ('constructor', 'toString', ...)
// cannot resolve through Object.prototype and leak a Function past `?? null`.
const GQL_CODE_TO_STATUS: Record<string, ErrorCodeType> = Object.assign(
  Object.create(null),
  {
    FORBIDDEN: ErrorCodeType.SessionExpired,
    UNAUTHENTICATED: ErrorCodeType.SessionExpired
  }
);

/**
 * Resolves a GraphQL error to a status code string.
 *
 * `extensions.originalError.statusCode` is present only when the subgraph threw a
 * NestJS HttpException (for example AuthGuard rejecting). Raw GraphQLErrors carry
 * `extensions.code` instead, so both shapes must be handled.
 *
 * Returns null when the error cannot be classified. Callers must not navigate to
 * an error page on null: an unrecognised error is not a server error.
 */
export function resolveGraphQlStatusCode(gqlError: any): string | null {
  const fromOriginalError = gqlError?.extensions?.originalError?.statusCode;
  if (fromOriginalError !== undefined && fromOriginalError !== null) {
    return String(fromOriginalError);
  }

  // Third shape seen in the wild: a bare `extensions.statusCode` on errors
  // arriving via HttpErrorResponse (the pre-existing catchError branch of
  // SessionInterceptor read exactly this).
  const fromExtensions = gqlError?.extensions?.statusCode;
  if (fromExtensions !== undefined && fromExtensions !== null) {
    return String(fromExtensions);
  }

  return GQL_CODE_TO_STATUS[gqlError?.extensions?.code] ?? null;
}
